import { useState, useMemo } from 'react';
import { Button, IconBadge, Label, Spinner, Switch } from '@monksflow/monks-ui';
import { PencilIcon } from 'lucide-react';
import { ProviderLogo } from './ProviderLogo';
import { RemoveProviderConfirmationModal } from '../RemoveProviderConfirmationModal';
import { useSaveProviderWhitelist } from '../../model';

import type {
  ModelProviderResponseDto,
  ModelProviderWhitelistResponseDto,
} from '@inference.engine/api-client/web';
import { useTranslation } from 'react-i18next';
import {
  PROVIDER_CARD_CLASS,
  PROVIDER_GRID_CLASS,
  PROVIDER_TILE_CLASS,
} from './ProviderWhitelistCard.styles';

interface ProviderWhitelistCardProps {
  whitelistedProviders: ModelProviderWhitelistResponseDto[];
  allProviders: ModelProviderResponseDto[];
}

/**
 * Renders the card component for viewing and updating organization level provider whitelists.
 */
export const ProviderWhitelistCard = ({
  whitelistedProviders,
  allProviders,
}: ProviderWhitelistCardProps) => {
  const { t } = useTranslation('admin');

  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedProviders, setSelectedProviders] = useState<string[]>([]);

  // Derive org-level providers (those without workspaceId)
  const orgLevelProviders = useMemo(() => {
    return whitelistedProviders.filter((p) => !p.workspaceId);
  }, [whitelistedProviders]);

  const {
    showConfirmation,
    affectedProviders,
    isProcessing,
    handleSave,
    handleConfirmRemove,
    handleCancelConfirmation,
  } = useSaveProviderWhitelist({
    orgLevelProviders,
    selectedProviders,
    allWhitelistedProviders: whitelistedProviders,
    onSaveSuccess: () => setIsEditMode(false),
  });

  const handleEditClick = () => {
    setSelectedProviders(orgLevelProviders.map((p) => p.key));
    setIsEditMode(true);
  };

  const handleCancelClick = () => {
    setSelectedProviders([]);
    setIsEditMode(false);
  };

  const handleToggleProvider = (providerKey: string) => {
    setSelectedProviders((prev) =>
      prev.includes(providerKey) ? prev.filter((p) => p !== providerKey) : [...prev, providerKey],
    );
  };

  const visibleProviders = isEditMode ? allProviders : orgLevelProviders;

  return (
    <>
      <section className={PROVIDER_CARD_CLASS}>
        <div className="flex items-center justify-between">
          <h2 className="text-base font-medium text-foreground leading-6">
            {t('admin.providerWhitelistCard.aiModelProviders')}
          </h2>
          {isEditMode ? (
            <div className="flex gap-2 items-center">
              <Button type="button" variant="outline" onClick={handleCancelClick}>
                {t('admin.providerWhitelistCard.cancel')}
              </Button>
              <Button
                type="button"
                onClick={() => void handleSave()}
                disabled={isProcessing || selectedProviders.length === 0}
              >
                {/* Hidden from the a11y tree so the button keeps its own name; the
                    disabled state is what announces that the save is in flight. */}
                {isProcessing && <Spinner aria-hidden="true" />}
                {t('admin.providerWhitelistCard.save')}
              </Button>
            </div>
          ) : (
            <Button type="button" variant="ghost" onClick={handleEditClick}>
              {t('admin.providerWhitelistCard.edit')}
              <PencilIcon className="text-muted-foreground" />
            </Button>
          )}
        </div>
        {visibleProviders.length > 0 ? (
          <ul className={PROVIDER_GRID_CLASS}>
            {visibleProviders.map((provider) => {
              const switchId = `provider-whitelist-${provider.key}`;
              const isEnabled = selectedProviders.includes(provider.key);
              // The name labels the switch, which only edit mode renders.
              // eslint-disable-next-line i18next/no-literal-string -- element name, not copy
              const ProviderName = isEditMode ? Label : 'span';
              return (
                <li key={provider.key} className={PROVIDER_TILE_CLASS}>
                  <div className="flex items-start justify-between gap-sm">
                    <IconBadge variant="outline" size="sm">
                      <ProviderLogo providerKey={provider.key} />
                    </IconBadge>
                    {isEditMode && (
                      <div className="flex items-center gap-xs">
                        <Switch
                          id={switchId}
                          checked={isEnabled}
                          onCheckedChange={() => handleToggleProvider(provider.key)}
                          disabled={isProcessing}
                        />
                        {/* The switch already announces its own state, so this would be read twice. */}
                        <span aria-hidden="true" className="type-body-sm text-muted-foreground">
                          {isEnabled
                            ? t('admin.providerWhitelistCard.enabled')
                            : t('admin.providerWhitelistCard.disabled')}
                        </span>
                      </div>
                    )}
                  </div>
                  <ProviderName
                    {...(isEditMode ? { htmlFor: switchId } : {})}
                    className="type-body-md-strong truncate text-foreground"
                  >
                    {provider.name}
                  </ProviderName>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="type-body-md text-muted-foreground">
            {t('admin.providerWhitelistCard.noProvidersSelected')}
          </p>
        )}
      </section>

      <RemoveProviderConfirmationModal
        open={showConfirmation}
        isProcessing={isProcessing}
        onCancel={handleCancelConfirmation}
        onConfirm={handleConfirmRemove}
        affectedProviders={affectedProviders}
      />
    </>
  );
};
